import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { EndPoints, Order } from '../../../common/enums/enums';
import { errorResponseHelper } from '../../../helpers/apiResponseHelpers';
import { amplifyApi } from '../../../hooks/amplifyApi';
import { newServiceOrderActionTypes } from '../../types/newServiceOrderTypes';

export const clearServiceOrder = createAction(
  newServiceOrderActionTypes.clearServiceOrder
);

export const discardServiceOrder = createAction(
  newServiceOrderActionTypes.discardServiceOrder
);

export const clearServiceOrderError = createAction(
  newServiceOrderActionTypes.clearServiceOrderError
);

export const setServiceOrderObservations = createAction(
  newServiceOrderActionTypes.setServiceOrderObservations
);

export const addServiceOrderSpecialRequest = createAction(
  newServiceOrderActionTypes.addServiceOrderSpecialRequest
);

export const setServiceOrderAlternativeOwner = createAction(
  newServiceOrderActionTypes.setServiceOrderAlternativeOwner
);

export const setServiceOrderPromiseDate = createAction(
  newServiceOrderActionTypes.setServiceOrderPromiseDate
);

export const updateServiceOrderCreatedDate = createAction(
  newServiceOrderActionTypes.updateServiceOrderCreatedDate
);

export const changeServiceOrderCompletionType = createAction(
  newServiceOrderActionTypes.changeServiceOrderCompletionType
);

export const setServiceOrderServicesComplianceTime = createAction(
  newServiceOrderActionTypes.setServiceOrderServicesComplianceTime
);

export const removeServiceOrderSpecialRequest = createAction(
  newServiceOrderActionTypes.removeServiceOrderSpecialRequest
);

export const setServiceOrderSelectedServicesPrices = createAction(
  newServiceOrderActionTypes.setServiceOrderSelectedServicesPrices,
  prices => ({ payload: prices })
);

export const openServiceOrderEditVehicleModal = createAction(
  newServiceOrderActionTypes.openServiceOrderEditVehicleModal
);

export const closeServiceOrderEditVehicleModal = createAction(
  newServiceOrderActionTypes.closeServiceOrderEditVehicleModal
);

export const setServiceOrderSelectedVendor = createAction(
  newServiceOrderActionTypes.setServiceOrderSelectedVendor
);

export const addPriceToSelectedServiceOrderPrices = createAction(
  newServiceOrderActionTypes.addPriceToSelectedServiceOrderPrices
);

export const removePriceToSelectedServiceOrderPrices = createAction(
  newServiceOrderActionTypes.removePriceToSelectedServiceOrderPrices
);

export const setAllServiceOrderDealers = createAsyncThunk(
  newServiceOrderActionTypes.setAllDealers,
  async companyId => {
    return await amplifyApi(
      null,
      EndPoints.ORDERS.DEALERS.GET.ENDPOINT.replace('{companyId}', companyId),
      EndPoints.ORDERS.DEALERS.GET.TYPE
    );
  }
);
export const setVehicleStockNumber = createAction(
  newServiceOrderActionTypes.setVehicleStockNumber
);

export const getServiceOrderServicesByDepartment = createAsyncThunk(
  newServiceOrderActionTypes.getServiceOrderServicesByDepartment,
  async ({ vendorId, department: { departmentId } }) => {
    return await amplifyApi(
      null,
      EndPoints.ORDERS.NEW_ORDER.GET_SERVICES_BY_DEPARTMENT.ENDPOINT.replace(
        '{vendorId}',
        vendorId
      ).replace('{departmentId}', departmentId),
      EndPoints.ORDERS.NEW_ORDER.GET_SERVICES_BY_DEPARTMENT.METHOD
    );
  }
);

export const getServiceOrderVehicleByVin = createAsyncThunk(
  newServiceOrderActionTypes.getServiceOrderVehicleByVin,
  async ({ vin }) => {
    return await getServiceOrderVehicle(vin);
  }
);

export const saveServiceOrder = createAsyncThunk(
  newServiceOrderActionTypes.saveServiceOrder,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await amplifyApi(
        payload,
        EndPoints.ORDERS.NEW_SERVICE_ORDER.SAVE.ENDPOINT,
        EndPoints.ORDERS.NEW_SERVICE_ORDER.SAVE.METHOD,
        true
      );
      return response.data;
    } catch (error) {
      const woDetails = error.response.data.data.activeOrders
        ? error.response.data.data.activeOrders[0]
        : error.response.data.data;

      return rejectWithValue({
        ...errorResponseHelper(error),
        data: { ...woDetails },
      });
    }
  }
);

export const editServiceOrderVehicleDetails = createAsyncThunk(
  newServiceOrderActionTypes.editServiceOrderVehicleDetails,
  async ({ data, vehicleId }) => {
    await amplifyApi(
      {
        make: data.make,
        model: data.model,
        year: data.year,
        color: data.color,
      },
      EndPoints.ORDERS.NEW_ORDER.EDIT_VEHICLE_DETAIL.ENDPOINT.replace(
        '{vehicleId}',
        vehicleId
      ),
      EndPoints.ORDERS.NEW_ORDER.EDIT_VEHICLE_DETAIL.METHOD
    );

    return await getServiceOrderVehicle(data.vin);
  }
);

export const addServiceOrderVehicleDetails = createAsyncThunk(
  newServiceOrderActionTypes.addServiceOrderVehicleDetails,
  async ({ data }) => {
    return await amplifyApi(
      {
        make: data.make,
        model: data.model,
        year: data.year,
        color: data.color,
        vin: data.vin,
      },
      EndPoints.ORDERS.NEW_ORDER.ADD_VEHICLE_DETAIL.ENDPOINT,
      EndPoints.ORDERS.NEW_ORDER.ADD_VEHICLE_DETAIL.METHOD
    );
  }
);

const getServiceOrderVehicle = vin =>
  amplifyApi(
    {},
    EndPoints.ORDERS.NEW_ORDER.GET_VEHICLE_BY_VIN.ENDPOINT.replace(
      '{vin}',
      vin
    ),
    EndPoints.ORDERS.NEW_ORDER.GET_VEHICLE_BY_VIN.METHOD
  );

export const serviceOrderAlternativeOwner = createAsyncThunk(
  newServiceOrderActionTypes.serviceOrderAlternativeOwner,
  async ({
    companyId,
    departmentId,
    pageSize = 100,
    pageNumber = 1,
    order = Order.ASC,
    filters = [],
    searchQuery = '',
  }) => {
    return await amplifyApi(
      {
        pageSize,
        pageNumber,
        order,
        filters,
        searchQuery,
        groupedFilters: {
          AND: [
            {
              AND: [
                {
                  column: 'companyId',
                  value: companyId,
                  operator: 'eq',
                },
                {
                  column: 'departmentId',
                  value: departmentId,
                  operator: 'eq',
                },
                {
                  column: 'status',
                  value: 'ACTIVE',
                  operator: 'eq',
                },
              ],
            },
          ],
        },
      },
      EndPoints.USER.ALTERNATIVE_OWNER.ENDPOINT,
      EndPoints.USER.ALTERNATIVE_OWNER.METHOD
    );
  }
);
